"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { LANDING_PARAM, landingParam } from "@/lib/analytics/demand";

// gclid/gbraid/wbraid: Google Ads. oppref: ChatGPT Ads (OpenAI pixel).
const CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid", "oppref"] as const;

// Module memory only — no cookie, no storage. Survives client-side
// navigation; full page loads are covered by decorating the link itself.
const captured: Partial<Record<(typeof CLICK_ID_PARAMS)[number], string>> = {};

function readClickIds() {
  const params = new URLSearchParams(window.location.search);
  for (const key of CLICK_ID_PARAMS) {
    const value = params.get(key);
    if (value) captured[key] = value;
  }
}

/** Adds the captured click ids, and the landing (utm_*, referrer, landing page;
 *  lib/analytics/demand.ts), to `url`. Returns true if anything was added. */
function decorate(url: URL): boolean {
  let changed = false;
  const landing = landingParam();
  if (landing && !url.searchParams.has(LANDING_PARAM)) {
    url.searchParams.set(LANDING_PARAM, landing);
    changed = true;
  }
  for (const key of CLICK_ID_PARAMS) {
    const value = captured[key];
    if (value && !url.searchParams.has(key)) {
      url.searchParams.set(key, value);
      changed = true;
    }
  }
  return changed;
}

/**
 * Keeps the Google Ads click id in the URL while the visitor moves around the
 * site.
 *
 * Most visitors ignore the cookie banner on the landing page and only grant
 * consent at the first "Fortsätt" in /boka. By then the gclid from the ad
 * click is gone from the URL, so GA4 and the Conversion Linker never see it
 * and the booking is not credited to the ad. This is a hand-rolled version of
 * Google's consent-mode `url_passthrough`: nothing is stored on the device,
 * the id just rides along in the URL until consent is granted.
 *
 * Two paths, because the site mixes both kinds of link:
 * - plain <a href> (all booking CTAs) → full page load → decorate the href in
 *   the capture phase, before the browser navigates.
 * - next/link → client-side navigation that ignores the DOM href → put the id
 *   back on the URL after the route change.
 */
export default function ClickIdPassthrough() {
  const pathname = usePathname();

  useEffect(() => {
    readClickIds();
    const url = new URL(window.location.href);
    if (decorate(url)) {
      window.history.replaceState(window.history.state, "", url);
    }
  }, [pathname]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      let url: URL;
      try {
        url = new URL(anchor.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.protocol !== "http:" && url.protocol !== "https:") return;
      if (decorate(url)) anchor.href = url.toString();
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return null;
}
